import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useProject } from "./useProject";

export interface MatrixConfig {
  row_id?: string;      // ID de la fila específica de la matriz
  column_id?: string;   // ID de la columna específica de la matriz
}

export interface Calculation {
  id: number;
  template_id: number;
  name: string;
  calculation_type: 'aggregate' | 'formula' | 'ranking';
  config: {
    // For aggregate and ranking
    operation: 'sum' | 'average' | 'count' | 'max' | 'min' | 'custom' | 'top' | 'bottom' | 'count_referrals';
    scope: 'main' | 'referral' | 'comparison' | 'code' | 'launch';
    formId?: string;
    group_name?: string;
    question_names?: string[];  // Deprecated: usar question_name en su lugar
    question_name?: string;
    matrix_config?: MatrixConfig;

    // Aggregation mode for matrix questions
    // 'nested': promedio de promedios por pregunta (default, actual)
    // 'flat': promedio de todos los valores individuales
    aggregation_mode?: 'nested' | 'flat';

    // For formula
    formula?: string;
    referral_id?: string;

    // For ranking
    limit?: number;              // Top N items (default: 5)
    format?: 'bullet' | 'numbered';  // Output format (default: 'bullet')
    show_values?: boolean;       // Show numeric values (default: true)

    // For code/launch scope
    code?: string;               // Usually '{current_code}'
    launch_id?: number;          // Usually '{current_launch_id}'

    // For grouping
    groupBy?: 'jotform_groups' | 'launch' | 'code';
  };
  created_at: string;
  updated_at: string;
}

export function useCalculations(templateId: number) {
  const { projectClient } = useProject();
  const queryClient = useQueryClient();

  // Query to get calculations
  const { data: calculations, isLoading } = useQuery({
    queryKey: ['template-calculations', templateId],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from('template_calculations')
        .select('*')
        .eq('template_id', templateId)
        .eq('project_id', projectClient.getProjectId())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching calculations:', error);
        throw error;
      }

      return data as Calculation[];
    },
    enabled: !!templateId,
  });

  // Mutation to create calculation
  const createCalculation = useMutation({
    mutationFn: async (calculationData: Omit<Calculation, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await projectClient
        .from('template_calculations')
        .insert({
          ...calculationData,
          project_id: projectClient.getProjectId(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-calculations', templateId] });
      toast.success('Cálculo creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al crear el cálculo');
    },
  });

  // Mutation to update calculation
  const updateCalculation = useMutation({
    mutationFn: async ({ id, ...calculationData }: Partial<Calculation> & { id: number }) => {
      const { data, error } = await projectClient
        .from('template_calculations')
        .update({
          ...calculationData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('project_id', projectClient.getProjectId())
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-calculations', templateId] });
      toast.success('Cálculo actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al actualizar el cálculo');
    },
  });

  // Mutation to delete calculation
  const deleteCalculation = useMutation({
    mutationFn: async (calculationId: number) => {
      const { error } = await projectClient
        .from('template_calculations')
        .delete()
        .eq('id', calculationId)
        .eq('project_id', projectClient.getProjectId());

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-calculations', templateId] });
      toast.success('Cálculo eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al eliminar el cálculo');
    },
  });

  return {
    calculations,
    isLoading,
    createCalculation: createCalculation.mutateAsync,
    updateCalculation: updateCalculation.mutateAsync,
    deleteCalculation: deleteCalculation.mutateAsync,
    isCreating: createCalculation.isPending,
    isUpdating: updateCalculation.isPending,
    isDeleting: deleteCalculation.isPending,
  };
}
